import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './SOCSSetupModal.css'; // Reuse SOCS modal styles

interface PitcheroSetupModalProps {
  isOpen: boolean;
  onClose: (completed: boolean, mappings?: any[]) => void;
  parentUuid: string;
  suggestedEmails?: string[];
  defaultEmail?: string;
  onEmailUsed?: (email: string) => void;
}

const PitcheroSetupModal: React.FC<PitcheroSetupModalProps> = ({ isOpen, onClose, parentUuid, suggestedEmails = [], defaultEmail = '', onEmailUsed }) => {
  const apiService = ApiService.getInstance();

  // Steps: 1 = Credentials, 2 = Connecting, 3 = Review Mappings
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Credentials
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showCustomEmailInput, setShowCustomEmailInput] = useState(false);

  // Step 3: Mappings review
  const [mappings, setMappings] = useState<any[]>([]);
  const [availableChildren, setAvailableChildren] = useState<any[]>([]);
  // Track which clubs are selected for import (key: mappingId-clubName, value: boolean)
  const [selectedClubs, setSelectedClubs] = useState<{ [key: string]: boolean }>({});
  // Track which teams are selected for import (key: mappingId-teamId, value: boolean)
  const [selectedTeams, setSelectedTeams] = useState<{ [key: string]: boolean }>({});
  // Track which children to exclude from import entirely
  const [excludedChildren, setExcludedChildren] = useState<{ [mappingId: number]: boolean }>({});

  // Track previous isOpen state to detect fresh open vs resume
  const wasOpen = React.useRef(false);

  // Reset state when modal opens (only on FRESH open, not resume from background)
  useEffect(() => {
    if (isOpen) {
      // Only reset state on FRESH open (was closed, now open)
      // Don't reset on resume from background (was open, still open)
      if (!wasOpen.current) {
        setStep(1);
        setError(null);
        setUsername(defaultEmail || ''); // Pre-populate with default email
        setPassword('');
        setShowPassword(false);
        setShowCustomEmailInput(false);
        setMappings([]);
        setAvailableChildren([]);
        setSelectedClubs({});
        setSelectedTeams({});
        setExcludedChildren({});
      }
      wasOpen.current = true;
    } else {
      wasOpen.current = false;
    }
  }, [isOpen, defaultEmail]);

  // Initialize selected clubs and teams when mappings are loaded
  useEffect(() => {
    if (mappings.length > 0) {
      const initialClubSelection: { [key: string]: boolean } = {};
      const initialTeamSelection: { [key: string]: boolean } = {};
      mappings.forEach(mapping => {
        if (mapping.clubs && mapping.clubs.length > 0) {
          mapping.clubs.forEach((club: string) => {
            initialClubSelection[`${mapping.id}-${club}`] = true; // All clubs selected by default
          });
        }
        // Initialize all teams as selected by default
        if (mapping.teams && mapping.teams.length > 0) {
          mapping.teams.forEach((team: { teamId: string; teamName: string; clubName: string }) => {
            initialTeamSelection[`${mapping.id}-${team.teamId}`] = true; // All teams selected by default
          });
        }
      });
      setSelectedClubs(initialClubSelection);
      setSelectedTeams(initialTeamSelection);
    }
  }, [mappings]);

  const toggleClubSelection = (mappingId: number, clubName: string) => {
    const key = `${mappingId}-${clubName}`;
    setSelectedClubs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleTeamSelection = (mappingId: number, teamId: string) => {
    const key = `${mappingId}-${teamId}`;
    setSelectedTeams(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isTeamSelected = (mappingId: number, teamId: string) => {
    const key = `${mappingId}-${teamId}`;
    return selectedTeams[key] !== false; // Default to true if not set
  };

  const getSelectedTeamsForMapping = (mappingId: number, teams: { teamId: string }[]) => {
    return teams.filter(team => isTeamSelected(mappingId, team.teamId));
  };

  const toggleChildExclusion = (mappingId: number) => {
    setExcludedChildren(prev => ({
      ...prev,
      [mappingId]: !prev[mappingId]
    }));
  };

  const isChildExcluded = (mappingId: number) => {
    return excludedChildren[mappingId] === true;
  };

  const isClubSelected = (mappingId: number, clubName: string) => {
    const key = `${mappingId}-${clubName}`;
    return selectedClubs[key] !== false; // Default to true if not set
  };

  const getSelectedClubsForMapping = (mappingId: number, clubs: string[]) => {
    return clubs.filter(club => isClubSelected(mappingId, club));
  };

  const handleConnect = async () => {
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError(null);
    setStep(2);

    try {
      // Save credentials
      const response = await apiService.savePitcheroCredentials(parentUuid, {
        username,
        password
      });

      if (!response.success) {
        setError(response.error || 'Failed to save credentials');
        setStep(1);
        setLoading(false);
        return;
      }

      // Trigger match-only sync
      const syncResponse = await apiService.triggerPitcheroSync(parentUuid, true);

      if (syncResponse.success) {
        const syncFailed = syncResponse.data?.sync_status === 'failed';
        const noChildrenFound = (syncResponse.data?.childrenMatched || 0) === 0 &&
                                (syncResponse.data?.clubsDiscovered || 0) === 0 &&
                                (!syncResponse.data?.unmatchedChildren || syncResponse.data.unmatchedChildren.length === 0);

        if (syncFailed || noChildrenFound) {
          setError('No children or clubs found. Please check your credentials and try again.');
          setStep(1);
          setUsername('');
          setPassword('');
          setLoading(false);
          return;
        }

        // Load mappings to review
        const mappingsResponse = await apiService.getPitcheroMappings(parentUuid);

        if (mappingsResponse.success && mappingsResponse.data && mappingsResponse.data.mappings.length > 0) {
          setMappings(mappingsResponse.data.mappings || []);
          setAvailableChildren(mappingsResponse.data.children || []);
          setStep(3);

          // Notify about custom email used (for reuse in other integrations)
          if (onEmailUsed && username && !suggestedEmails.includes(username)) {
            onEmailUsed(username);
          }
        } else {
          // No mappings but sync didn't fail
          onClose(true, []);
        }
      } else {
        setError(syncResponse.error || 'Sync failed. Please check your credentials.');
        setStep(1);
        setUsername('');
        setPassword('');
      }
    } catch (err: any) {
      console.error('Pitchero connection error:', err);
      setError(err.message || 'Failed to connect to Pitchero');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = async (mappingId: number, newChildUuid: string) => {
    try {
      const response = await apiService.updatePitcheroMapping(parentUuid, mappingId, newChildUuid);

      if (response.success) {
        setMappings(prevMappings =>
          prevMappings.map(m =>
            m.id === mappingId
              ? {
                  ...m,
                  mappedChildUuid: newChildUuid,
                  mappedChildName: availableChildren.find(c => c.uuid === newChildUuid)?.name || ''
                }
              : m
          )
        );
      } else {
        setError('Failed to update mapping');
      }
    } catch (err) {
      console.error('Failed to update mapping:', err);
      setError('Failed to update mapping');
    }
  };

  const handleApproveAndImport = async () => {
    setLoading(true);
    setError(null);

    try {
      // Collect excluded clubs and selected teams for each mapping (skip entirely excluded children)
      const excludedClubs: { [mappingId: number]: string[] } = {};
      const excludedChildMappingIds: number[] = [];
      const selectedTeamsByMapping: { [mappingId: number]: string[] } = {};

      mappings.forEach(mapping => {
        // If child is excluded entirely, add all their clubs to excluded and track mapping ID
        if (isChildExcluded(mapping.id)) {
          excludedChildMappingIds.push(mapping.id);
          if (mapping.clubs && mapping.clubs.length > 0) {
            excludedClubs[mapping.id] = [...mapping.clubs]; // Exclude all clubs for this child
          }
        } else {
          // Track individually deselected clubs
          if (mapping.clubs && mapping.clubs.length > 0) {
            const excluded = mapping.clubs.filter((club: string) => !isClubSelected(mapping.id, club));
            if (excluded.length > 0) {
              excludedClubs[mapping.id] = excluded;
            }
          }

          // Collect selected teams for this mapping
          if (mapping.teams && mapping.teams.length > 0) {
            const selected = mapping.teams
              .filter((team: { teamId: string }) => isTeamSelected(mapping.id, team.teamId))
              .map((team: { teamId: string }) => team.teamId);
            selectedTeamsByMapping[mapping.id] = selected;
          }
        }
      });

      console.log('Excluded children (mapping IDs):', excludedChildMappingIds);
      console.log('Excluded clubs:', excludedClubs);
      console.log('Selected teams:', selectedTeamsByMapping);

      const importResponse = await apiService.importPitcheroActivities(parentUuid, excludedClubs, excludedChildMappingIds, selectedTeamsByMapping);

      if (importResponse.success) {
        onClose(true, mappings);
      } else {
        setError(importResponse.error || 'Import failed');
      }
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import activities');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="socs-modal-overlay">
      <div className="socs-modal">
        <div className="socs-modal-header" style={{
          background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
          borderRadius: '8px 8px 0 0'
        }}>
          <h2 style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>🏟️ Connect Pitchero</h2>
          <button className="close-btn" onClick={() => onClose(false)} style={{ color: 'white', opacity: 0.8 }}>×</button>
        </div>

        <div className="socs-modal-content">
          {error && (
            <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
              {error}
            </div>
          )}

          {/* Step 1: Credentials */}
          {step === 1 && (
            <>
              <p style={{ marginBottom: '16px' }}>
                Enter your Pitchero login details. We'll discover your clubs, match your children,
                and import all fixtures and training sessions.
              </p>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Pitchero Email/Username</label>
                {suggestedEmails.length > 0 && !showCustomEmailInput ? (
                  <select
                    value={username}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setShowCustomEmailInput(true);
                        setUsername('');
                      } else {
                        setUsername(e.target.value);
                      }
                    }}
                    disabled={loading}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', backgroundColor: 'white' }}
                  >
                    <option value="">Select an email...</option>
                    {suggestedEmails.map((email, idx) => (
                      <option key={idx} value={email}>{email}</option>
                    ))}
                    <option value="__custom__">+ Add different email</option>
                  </select>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="form-input"
                      placeholder="your.email@example.com"
                      disabled={loading}
                    />
                    {suggestedEmails.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomEmailInput(false);
                          setUsername(defaultEmail || suggestedEmails[0] || '');
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 10px',
                          fontSize: '12px',
                          color: '#28a745',
                          background: 'none',
                          border: '1px solid #28a745',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Back to saved emails
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Pitchero Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Your Pitchero password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <a href="https://www.pitchero.com/login" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: '13px', color: '#1976d2', textDecoration: 'none', display: 'inline-block', marginTop: '6px' }}>
                  Forgot password? →
                </a>
              </div>

              <button
                onClick={handleConnect}
                disabled={loading || !username || !password}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: (!loading && username && password) ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!loading && username && password) ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  marginTop: '8px'
                }}
              >
                {loading ? '⏳ Connecting...' : '🔗 Connect Pitchero'}
              </button>

              <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '12px' }}>
                🔒 Your credentials are encrypted and stored securely
              </p>
            </>
          )}

          {/* Step 2: Connecting */}
          {step === 2 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e0e0e0',
                borderTop: '4px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              <p style={{ fontSize: '16px', fontWeight: 500 }}>
                Connecting to Pitchero...
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Discovering clubs and matching children
              </p>
            </div>
          )}

          {/* Step 3: Review Mappings */}
          {step === 3 && (
            <>
              <p style={{ marginBottom: '16px' }}>
                We've automatically matched your Pitchero children with your Cluubz children.
                Please verify these are correct:
              </p>

              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                {mappings.map((mapping) => (
                  <div
                    key={mapping.id}
                    style={{
                      border: isChildExcluded(mapping.id) ? '1px solid #ccc' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      backgroundColor: isChildExcluded(mapping.id) ? '#f5f5f5' : 'white',
                      opacity: isChildExcluded(mapping.id) ? 0.7 : 1
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                        <strong style={{ color: isChildExcluded(mapping.id) ? '#999' : 'inherit' }}>
                          {mapping.pitcheroChildName}
                        </strong>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: isChildExcluded(mapping.id) ? '#dc3545' : '#28a745',
                            flexShrink: 0
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!isChildExcluded(mapping.id)}
                            onChange={() => toggleChildExclusion(mapping.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          {isChildExcluded(mapping.id) ? "Don't import" : 'Import fixtures'}
                        </label>
                      </div>
                      {mapping.confidenceScore && !isChildExcluded(mapping.id) && (
                        <span
                          style={{
                            display: 'inline-block',
                            marginTop: '4px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor:
                              mapping.confidenceScore >= 0.9
                                ? '#4CAF50'
                                : mapping.confidenceScore >= 0.7
                                ? '#FF9800'
                                : '#F44336',
                            color: 'white'
                          }}
                        >
                          {Math.round(mapping.confidenceScore * 100)}% match
                        </span>
                      )}
                    </div>

                    {!isChildExcluded(mapping.id) && ((mapping.clubs && mapping.clubs.length > 0) || (mapping.teams && mapping.teams.length > 0)) && (
                      <div style={{
                        marginBottom: '8px',
                        padding: '8px 10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: 500, marginBottom: '6px', color: '#555' }}>
                          🏟️ {mapping.teams && mapping.teams.length > 0 ? 'Select teams to import:' : 'Clubs found:'}
                        </div>
                        {/* Group teams by club. Show clubs even when no teams are
                            assigned yet — the child may be in a club but not on a
                            team (e.g. netball without a squad yet). */}
                        {(mapping.clubs && mapping.clubs.length > 0
                          ? mapping.clubs
                          : Array.from(new Set((mapping.teams || []).map((t: any) => t.clubName).filter(Boolean)))
                        ).map((clubName: string) => {
                          const clubTeams = (mapping.teams || []).filter((t: { clubName: string }) => t.clubName === clubName);
                          return (
                            <div key={clubName} style={{ marginBottom: '8px' }}>
                              <div style={{ fontWeight: 500, fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                {clubName}
                              </div>
                              {clubTeams.length > 0 ? clubTeams.map((team: { teamId: string; teamName: string; clubName: string }) => (
                                <label
                                  key={team.teamId}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px 0 4px 12px',
                                    cursor: 'pointer',
                                    color: isTeamSelected(mapping.id, team.teamId) ? '#333' : '#999',
                                    textDecoration: isTeamSelected(mapping.id, team.teamId) ? 'none' : 'line-through'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isTeamSelected(mapping.id, team.teamId)}
                                    onChange={() => toggleTeamSelection(mapping.id, team.teamId)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                  {team.teamName}
                                </label>
                              )) : (
                                <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic', padding: '2px 0 2px 12px' }}>
                                  No team assigned yet
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {mapping.teams && mapping.teams.length > 0 && getSelectedTeamsForMapping(mapping.id, mapping.teams).length === 0 && (
                          <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                            ⚠️ No teams selected - this child will be skipped
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show warning only when neither clubs nor teams are available. */}
                    {!isChildExcluded(mapping.id) && (!mapping.teams || mapping.teams.length === 0) && (!mapping.clubs || mapping.clubs.length === 0) && (
                      <div style={{
                        marginBottom: '8px',
                        padding: '8px 10px',
                        backgroundColor: '#fff3cd',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#856404'
                      }}>
                        ⚠️ No club or team information available - fixtures may not import correctly
                      </div>
                    )}

                    {!isChildExcluded(mapping.id) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#666', fontSize: '13px' }}>Maps to:</span>
                        <select
                          value={mapping.mappedChildUuid}
                          onChange={(e) => handleMappingChange(mapping.id, e.target.value)}
                          style={{
                            flex: 1,
                            padding: '6px 10px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '13px'
                          }}
                        >
                          {availableChildren.map((child) => (
                            <option key={child.uuid} value={child.uuid}>
                              {child.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {isChildExcluded(mapping.id) && (
                      <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
                        This child's fixtures will not be imported
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleApproveAndImport}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: loading ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {loading ? '⏳ Importing...' : '✓ Approve & Import Activities'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PitcheroSetupModal;
